import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db7meavidngsrf4ehn2g";

interface Status {
  state: "idle" | "submitting" | "success" | "error";
  message: string;
}

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>({ state: "idle", message: "" });
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus({ state: "success", message: "感谢你的反馈，我们已收到你的留言。" });
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      payload.append(key, String(value));
    });

    setFormError("");
    setStatus({ state: "submitting", message: "" });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const code = parsed?.code;

      if (response.ok && code === "OK" && !isSpam) {
        setStatus({ state: "success", message: "感谢你的反馈，我们已收到你的留言。" });
        formEl.reset();
      } else {
        const message = serverMsg && !isSpam ? serverMsg : "提交失败，请稍后重试。";
        setFormError(message);
        setStatus({ state: "error", message });
      }
    } catch {
      const message = "网络异常，提交失败，请稍后重试。";
      setFormError(message);
      setStatus({ state: "error", message });
    }
  };

  return (
    <form
      data-readdy-form
      id="about-feedback-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200/70 bg-background-50 p-6"
    >
      <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
        <i className="ri-chat-smile-2-line text-primary-700" />
        用户反馈留言
      </h2>
      <p className="mt-2 text-sm text-foreground-600">
        对影片信息、功能体验或内容建议有任何想法，都欢迎通过下方表单告诉我们。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground-700">你的称呼</span>
          <input
            type="text"
            name="name"
            placeholder="请输入称呼"
            className="rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-500"
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground-700">联系邮箱</span>
          <input
            type="email"
            name="email"
            required
            placeholder="you@example.com"
            className="rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-500"
          />
        </label>

        <label className="flex flex-col gap-1.5 sm:col-span-2">
          <span className="text-sm font-medium text-foreground-700">反馈类型</span>
          <select
            name="topic"
            className="cursor-pointer rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400"
          >
            <option value="内容建议">内容建议</option>
            <option value="影片信息纠错">影片信息纠错</option>
            <option value="功能体验反馈">功能体验反馈</option>
            <option value="合作洽谈">合作洽谈</option>
          </select>
        </label>

        <label className="flex flex-col gap-1.5 sm:col-span-2">
          <span className="text-sm font-medium text-foreground-700">留言内容（最多 500 字）</span>
          <textarea
            name="message"
            required
            maxLength={500}
            rows={5}
            placeholder="请输入你的反馈内容"
            className="resize-none rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-500"
          />
        </label>
      </div>

      <input
        type="text"
        name="contact_alt"
        className="auxiliary-note-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="submit"
          disabled={status.state === "submitting"}
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className={status.state === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
          {status.state === "submitting" ? "提交中…" : "提交反馈"}
        </button>

        {status.state === "success" && (
          <span className="flex items-center gap-1.5 text-sm text-secondary-700">
            <i className="ri-checkbox-circle-line" />
            {status.message}
          </span>
        )}
        {status.state === "error" && (
          <span className="flex items-center gap-1.5 text-sm text-accent-700">
            <i className="ri-error-warning-line" />
            {formError || status.message}
          </span>
        )}
      </div>
    </form>
  );
}